import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { TokenService } from '@cm/ui-modules';
import { catchError, switchMap, throwError } from 'rxjs';

/**
 * HTTP Interceptor for /ai/** API requests.
 * Adds JWT Bearer token from TokenService.
 * On 401, automatically refreshes the token and retries once.
 */
export const aiJwtInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('/ai/') || req.url.includes('/auth/login')) {
    return next(req);
  }

  const tokenService = inject(TokenService);
  const token = tokenService.getAccessToken();

  const authReq = token ? _withBearer(req, token) : req;

  if (!token) {
    console.warn('[AI JWT Interceptor] No access token available, attempting refresh before request:', req.url);
  }

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401) {
        return throwError(() => err);
      }

      // On 401: try to refresh the token and retry once
      if (!tokenService.getRefreshToken()) {
        console.warn('[AI JWT Interceptor] 401 and no refresh token available for:', req.url);
        return throwError(() => err);
      }

      return tokenService.refreshSession().pipe(
        switchMap((data: any) => {
          const newToken = data?.access_token ?? tokenService.getAccessToken();
          if (!newToken) {
            return throwError(() => err);
          }
          return next(_withBearer(req, newToken));
        }),
        catchError(() => throwError(() => err)),
      );
    }),
  );
};

function _withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
